import { Component, Inject, Injector, ViewChild } from '@angular/core';
import { TranslateScopes } from '@cloud-twin-v2/cloud-twin-shared/core/translate-scopes';
import { ElementTypeCategoriesDto } from '@cloud-twin-v2/generated/simulation.generated.client';
import { WidgetSettingsToken } from '@common-modules/dynamic-layout/dynamic-layout-external-settings';
import { DynamicLayoutConfigurableItemSettings } from '@common-modules/dynamic-layout/models/dynamic-layout-configurable-item-settings';
import { SetValueAction } from '@common-modules/dynamic-layout/state/generic/generic.actions';
import { GetValueSelector } from '@common-modules/dynamic-layout/state/generic/generic.selectors';
import { BaseDynamicWidgetComponent } from '@common-modules/redux/components/base-dynamic-widget.component';
import { StateWidgetSettings } from '@common-modules/redux/models/state-widget-settings';
import { ReduxStateService } from '@common-modules/redux/redux-state.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { forkJoin } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MasterOperatorSingleSettings } from '../master-operator-single/master-operator-single-settings';
import { MasterOperatorSingleComponent } from '../master-operator-single/master-operator-single.component';
import { MasterOperatorConnectItemsSettings } from '../models/master-operator-connect-settings';
import { MasterOperatorFields } from '../models/master-operator-fields.enum';
import { ScenariosMetadata } from '../models/scenarios-metadata';
import { MasterOperatorService } from '../services/master-operator.service';
import { ScenarioInfoService } from '../services/scenario-info.service';

const COMPONENT_SELECTOR = 'tc-master-operator-single-widget';

@UntilDestroy()
@Component({
    selector: COMPONENT_SELECTOR,
    templateUrl: './master-operator-single-widget.component.html',
    styleUrls: ['./master-operator-single-widget.component.scss'],
    providers: [ReduxStateService],
    standalone: false
})
export class MasterOperatorSingleWidgetComponent extends BaseDynamicWidgetComponent {
  @ViewChild(MasterOperatorSingleComponent) singleComponent: MasterOperatorSingleComponent;

  widgetInstanceKey: string;
  settings: MasterOperatorSingleSettings;
  widgetSettings: StateWidgetSettings;

  readonly T_SCOPE = `${TranslateScopes.MasterOperator}.${COMPONENT_SELECTOR}`;
  connectSettings: MasterOperatorConnectItemsSettings;
  elementTypesCategories: Map<string, ElementTypeCategoriesDto[]>;

  constructor(
    injector: Injector,
    @Inject(WidgetSettingsToken) widgetSettings: StateWidgetSettings,
    private _masterOperatorService: MasterOperatorService,
    private _scenarioInfoService: ScenarioInfoService
  ) {
    super(injector, widgetSettings);
    this.widgetInstanceKey = widgetSettings.widgetInstanceKey;
    this.settings = widgetSettings.params;
    this.widgetSettings = widgetSettings;
  }

  onWidgetInit(): void {
    if (this.settings.connect) {
      this.onConfigureConnection(this.settings.connect);
    }

    this._state
      .select<MasterOperatorConnectItemsSettings>(
        new GetValueSelector({
          fieldName: MasterOperatorFields.ConnectSettings,
        })
      )
      .pipe(
        untilDestroyed(this),
        filter((data) => {
          return !!data && data.singleId == this.itemSettings.params.singleId;
        })
      )
      .subscribe((connectSettings: MasterOperatorConnectItemsSettings) => {
        this.connectSettings = connectSettings;
        this.singleComponent?.onConfigureConnection(connectSettings);
      });
  }

  get componentName(): string {
    return 'MasterOperatorSingleWidgetComponent';
  }

  onConfigureConnection(connectSettings: MasterOperatorConnectItemsSettings) {
    this.connectSettings = connectSettings;

    // Update the title and the settings that are saved in dynamic layout.
    this._masterOperatorService.getTitle$(connectSettings).subscribe((newTitle) => {
      const configurableItemSettings: DynamicLayoutConfigurableItemSettings = {
        widgetInstanceKey: this.widgetInstanceKey,
        params: {
          ...this.settings,
          connect: connectSettings,
        } as MasterOperatorSingleSettings,
        title: newTitle,
      };
      setTimeout(() => {
        this.updateItemSettings(configurableItemSettings);
      });
    });

    this.loadInfoService();
  }

  private loadInfoService() {
    const metadata$ = this.connectSettings.items.map((snapshotScenario) => {
      return this._scenarioInfoService.getScenarioAttributesInfo(
        snapshotScenario.snapshotId,
        snapshotScenario.scenarioId
      );
    });

    forkJoin(metadata$).subscribe({
      next: (metaData) => {
        const metaDataByScenarioSimulation = new Map<string, ElementTypeCategoriesDto[]>();

        metaData.forEach((x) => {
          metaDataByScenarioSimulation[`${x.snapshotId}-${x.scenarioId}`] =
            x.elementTypesCategories;
        });

        this.elementTypesCategories = metaDataByScenarioSimulation;

        this._state.dispatch(
          new SetValueAction({
            fieldName: MasterOperatorFields.AttributesMetadata,
            value: new ScenariosMetadata({
              metadata: metaDataByScenarioSimulation,
              singleId: this.itemSettings.params.singleId,
            }),
          })
        );
      },
      error: (error) => {}, // @Victor :C
    });
  }
}
